import React, { useState } from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import Icon from "../icons/Icon";
import Label from "./Label";
import ErrorsForField from "../form/ErrorsForField";
import { useField } from "../form/Form";

import "./InputAttachment.scss";

const InputAttachment = ({
  id,
  name,
  label,
  labelElement,
  value,
  maxLength,
  className,
  disabled,
  onChange,
  onBlur,
  onKeyDown,
  onValidate,
  required,
  hideLabel,
  noLabel,
  requiredError,
  hideErrors,
  fieldErrors,
  ...rest
}) => {
  useField({
    name,
    validation: { required, requiredError },
    onValidate,
    label
  });

  // display file name when file is selected
  const [fileNameDisplay, setFileNameDisplay] = useState("No File Selected");
  const [hasFile, setHasFile] = useState(false);
  const [keyId, setKeyId] = useState();

  const resetAttachment = () => {
    setFileNameDisplay("No File Selected");
    setKeyId(Math.random().toString(36));
    setHasFile(false);
    // eslint-disable-next-line no-restricted-globals
    onChange({ event, field: name, value: "" });
  };
  const containerClasses = classnames(
    "uikit__input-wrapper",
    "uikit__form-item",
    "uikit__input-attachment",
    className,
    {
      "uikit__hide-label": hideLabel,
      "uikit__input-attachment__file-selected": hasFile,
      "uikit__input-attachment__disabled": disabled,
      "uikit__form__hide-errors": hideErrors
    }
  );
  const inputClass = classnames("uikit__input-attachment__input", {
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value
  });

  const handleChange = event => {
    const file = event.target.files[0];
    if (file?.name) {
      setFileNameDisplay(file.name);
      setHasFile(true);
    }
    if (onChange) {
      onChange({ event, field: name, value: event.currentTarget.value });
    }
  };

  const handleBlur = event => {
    onBlur({ event, field: name, value: event.currentTarget.value });
  };

  const handleKeyDown = event => {
    if (onKeyDown) {
      onKeyDown({ event, value: event.currentTarget.value });
    }
  };

  return (
    <div className={containerClasses}>
      {!noLabel && (
        <Label
          htmlFor={id}
          label={label}
          labelElement={labelElement}
          required={required}
        />
      )}
      <div className="uikit__input-attachment__input-wrapper">
        <input
          key={keyId || ""}
          type="file"
          id={id}
          name={name}
          value={value}
          className={inputClass}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          disabled={disabled}
          {...rest}
          aria-describedby={`errors_for_${id}`}
        />
        <label className="uikit__input-attachment__button" htmlFor={id}>
          Select Attachment
        </label>
        <span className="uikit__input-attachment__file-name">
          {fileNameDisplay}
        </span>
        {hasFile && (
          <button
            onClick={resetAttachment}
            className="uikit__icon-wrapper uikit__input-attachment__reset"
          >
            <Icon icon="fasTimes" />
          </button>
        )}
      </div>
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

InputAttachment.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.string.isRequired,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,

  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific
  maxLength: propTypes.number
};

InputAttachment.defaultProps = {
  maxLength: 250,
  required: false,
  hideErrors: false
};

export default InputAttachment;
